/* eslint-disable max-lines */
// deps
import { recordNREvent } from "@checkout-core/newrelic";
import camelcaseKeys from "camelcase-keys";

// internal deps
import { NREvent, SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import { isAbortError, isConnectionError, isNetworkError } from "@swiggy-private/core-ui";
import { isEmpty, isString, getPayloadSize } from "@swiggy-private/js-utils";
import { ApiResponse } from "@swiggy-private/js-utils/dist/fetch/types";

// utils/helpers
import { GlobalHeaderState, showNativeAlert, SwgyHeaderType } from "@checkout-core/utils";
import { filterValidHeaders } from "./Http";

// interfaces/types
import { ApiCallParams, FetchDataReturnType } from "@checkout-core/interfaces/Api";

// constants
import { DEFAULT_API_RETRY_DELAY_TIME_IN_MS, DOWNSTREAM_TRIGGER_SOURCE } from "@checkout-core/constants/ApiTypes";
import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";
import { ERROR_MESSAGES, HTTP_ERROR_TEMPLATES, HTTP_STATUS_CODES } from "@checkout-core/constants/DowstreamAPICalls";
import { getSwiggyHeaders } from "./getSwiggyHeaders";

export async function sleep(ms: number): Promise<unknown> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export type Headers = { [header: string]: string | string[] | undefined };
export type SwgyHeaders = { [header: string]: string };
export type HttpError = Error & { httpStatus?: number };

export function nrErrorPlaceOrderSwiggyOneBL(
    request: { url: string },
    options: RequestInit,
    params: ApiCallParams,
    headers: Record<string, unknown>,
) {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_NETWORK_ERROR,
        params: (options && JSON.stringify(params)) || "",
        options: (options && JSON.stringify(options)) || "",
        headers: (headers && JSON.stringify(headers)) || "",
    });
}

function getErrorMessage(err: unknown): string {
    try {
        if (err instanceof Response) {
            // For HTTP Response errors, try to extract meaningful message
            if (err.status >= HTTP_STATUS_CODES.HTTP_STATUS_ERROR) {
                return err.statusText || HTTP_ERROR_TEMPLATES.HTTP_STATUS_ERROR(err.status);
            }
            return err.statusText || HTTP_ERROR_TEMPLATES.HTTP_STATUS(err.status);
        }

        if (err instanceof Error) {
            return HTTP_ERROR_TEMPLATES.ERROR_WITH_MESSAGE(err.message);
        }

        if (isString(err)) {
            return err;
        }

        return ERROR_MESSAGES.NETWORK_CONNECTION_ERROR;
    } catch (_err) {
        return ERROR_MESSAGES.NETWORK_CONNECTION_ERROR;
    }
}

export async function fetchData<T>(
    params: ApiCallParams,
    downStreamTriggerSource: string,
    apiRetriedCount = 0,
): Promise<FetchDataReturnType<T>> {
    const { retry = true, retryCount = 2, excludeDefaultHeaders = false } = params;
    const headers = { "Content-Type": "application/json", ...GlobalHeaderState.getHeaders() } as SwgyHeaderType;
    const timeoutValue = params.timeout ?? 20000;
    const apiHeaders = params.options?.headers ?? {};

    if (!isEmpty(headers.location_context)) {
        let cartAddressId = params.bodyParams?.address_id;
        if (isEmpty(params.bodyParams?.address_id)) {
            cartAddressId = headers?.cartaddressid;
        }

        if (cartAddressId !== headers?.addressid) {
            delete (headers as Partial<SwgyHeaderType>).location_context;
        }
    }

    const _requestHeaders =
        downStreamTriggerSource === DOWNSTREAM_TRIGGER_SOURCE.WEB_APP
            ? { "Content-Type": "application/json", ...getSwiggyHeaders(headers) }
            : headers;

    const requestHeaders = params.options?.headers
        ? { ..._requestHeaders, ...params.options.headers }
        : _requestHeaders;

    const options: RequestInit = {
        ...params.options,
        // filter out the headers with empty values and return only valid headers with some value
        headers: excludeDefaultHeaders ? apiHeaders : filterValidHeaders(requestHeaders), //  in cart-client we are passing filterValidHeaders(requestHeaders)
    };

    if (params.bodyParams) {
        options.body = JSON.stringify(params.bodyParams);
    }

    const searchParams = params.searchParams;
    let url = `${params.apiHost}${params.endpoint}`;
    if (searchParams) {
        url += `?${new URLSearchParams(searchParams).toString()}`;
    }

    const startTime = Date.now();

    let timeoutRef = null;

    if (typeof AbortController !== "undefined") {
        const controller = new AbortController();
        const signal = controller.signal;
        options.signal = signal;

        timeoutRef = setTimeout(() => {
            const diff = Date.now() - startTime;
            if (diff >= timeoutValue) {
                controller.abort();
            }
        }, timeoutValue);
    }

    const _url = url.toString();
    const _logUrl = _url.replace(params.apiHost, "");

    try {
        const response = await fetch(_url, {
            method: "GET",
            mode: "cors",
            headers,
            credentials: "omit",
            ...options,
        });

        timeoutRef && clearTimeout(timeoutRef);

        if (!response.ok) {
            let bodyMessage: string | undefined;
            try {
                const errorBody = await response.json();
                bodyMessage = errorBody?.message || errorBody?.statusMessage;
            } catch {
                // body is not JSON, fall back to statusText
            }
            const httpError = new Error(bodyMessage || response.statusText || `HTTP ${response.status}`);
            (httpError as HttpError).httpStatus = response.status;
            throw httpError;
        }

        // Calculate response size before consuming the response
        const responseSize = await getPayloadSize(response);
        const responseJson = await response.json();

        return {
            startTime,
            options,
            response: responseJson,
            logUrl: _logUrl,
            statusCode: response.status,
            statusMessage: response.statusText,
            apiRetriedCount,
            responseSize,
            extraNRData: params.extraNRData,
        };
    } catch (err) {
        timeoutRef && clearTimeout(timeoutRef);
        if ((isNetworkError(err) || isConnectionError(err) || isAbortError(err)) && retry && retryCount > 0) {
            await sleep(DEFAULT_API_RETRY_DELAY_TIME_IN_MS);
            return fetchData({ ...params, retryCount: retryCount - 1 }, downStreamTriggerSource, apiRetriedCount + 1);
        } else {
            nrErrorLog({ startTime, url: _logUrl }, err, downStreamTriggerSource, apiRetriedCount);

            const errorMessage = getErrorMessage(err);

            // Show Dialog for api failure due to network error
            showNetworkError(err);
            return {
                startTime,
                options,
                response: {} as T,
                logUrl: _logUrl,
                statusCode: 1,
                statusMessage: errorMessage,
                apiRetriedCount,
                responseSize: 0,
                extraNRData: params.extraNRData,
            };
        }
    }
}

let nativeNetworkErrorAlertVisible = false;

export function showNetworkError(err: unknown): void {
    if (!isNetworkError(err) || nativeNetworkErrorAlertVisible) {
        return;
    }

    nativeNetworkErrorAlertVisible = true;
    showNativeAlert({
        title: "CONNECTION ERROR",
        message: "Please check your network connectivity and try again",
    })
        .then((_res) => {
            nativeNetworkErrorAlertVisible = false;
        })
        .catch((_err) => {
            nativeNetworkErrorAlertVisible = false;
        });
}

export function recordSessionData<T>(response: ApiResponse<T>, url: string): void {
    const existingSid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "";
    const existingTid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.tid) || "";

    // If response object is null, no need to update session data.
    if (!response) {
        return;
    }

    const { tid, sid } = response;

    if (!isEmpty(tid)) {
        // record NR event in case of change in tid
        if (existingTid !== tid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.tid, tid);
    }

    if (!isEmpty(sid)) {
        // record NR event in case of change in sid
        if (existingSid !== sid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
                sid,
                existingSid,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.sid, sid);
    }
}

export async function nrInterceptor<T>(
    request: { startTime: number; url: string; opts: RequestInit; responseSize?: number },
    response: ApiResponse<T>,
): Promise<ApiResponse<T>> {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        statusCode: response.statusCode ?? -1,
        statusMessage: response.statusMessage ?? "",
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
        mId: String((request?.opts?.headers as Headers)?.["m_id"]),
    });

    //add logic for updating tid and sid
    recordSessionData(response, request.url);

    return response;
}

export async function nrDownstreamInterceptor<T>(
    request: {
        startTime: number;
        url: string;
        opts: RequestInit;
        responseSize?: number;
        extraNRData?: { [key: string]: unknown };
    },
    response: Response<T> | T,
    referNestedResponseForLogging?: boolean,
    apiRetriedCount?: number,
): Promise<Response<T> | T> {
    const typedResponse = referNestedResponseForLogging
        ? ((response as Response).response as T & ApiResponse<T>)
        : (response as T & ApiResponse<T>);
    let event: NREvent = {
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        apiRetriedCount: apiRetriedCount ?? 0,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
        mId: String((request?.opts?.headers as Headers)?.["m_id"]),
    };
    if (response instanceof Object && !isEmpty(response)) {
        event = {
            ...event,
            statusCode: typedResponse.statusCode ?? "--",
            statusMessage: typedResponse.statusMessage ?? "-",
            hasData: `${!isEmpty(typedResponse?.data)}`,
        };
    }
    if (request.extraNRData && !isEmpty(request.extraNRData)) {
        event = {
            ...event,
            extraNRData: JSON.stringify(request.extraNRData),
        };
    }
    recordNREvent(event);
    return response;
}

export function nrErrorLog(
    request: { startTime: number; url: string },
    err: unknown,
    downStreamTriggerSource = "",
    apiRetriedCount = 0,
): void {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_ERROR,
        responseTime: Date.now() - request.startTime,
        error: err instanceof Error ? err.name : JSON.stringify(err),
        errorMessage: err instanceof Error ? err.message : JSON.stringify(err),
        httpStatusCode: err instanceof Response ? err.status : ((err as HttpError).httpStatus ?? ""),
        downStreamTriggerSource,
        apiRetriedCount: apiRetriedCount ?? 0,
    });
}

export interface Response<T = any> {
    statusCode: number;
    statusMessage?: string | null;
    data?: T | null;
    rawResponse?: Response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

/**
 * @deprecated Use makeDownstreamApiCall instead. This function will be removed in a future
 */
export async function makeDownstreamApiCallDeprecated<T extends object = object>(
    params: ApiCallParams,
    transformToCamelCase?: boolean,
): Promise<Response<T> | T> {
    const response = await fetchData<T>(params, DOWNSTREAM_TRIGGER_SOURCE.WEB_APP);
    const { response: data, startTime, logUrl, options, apiRetriedCount, responseSize, extraNRData } = response;
    if (transformToCamelCase) {
        const rawResponse = Object.assign({}, response);
        const camelTransformedRespone: Response<T> = camelcaseKeys(response, { deep: true }) as Response<T>;
        camelTransformedRespone.rawResponse = rawResponse;
        return nrDownstreamInterceptor(
            { startTime, url: logUrl, opts: options, responseSize, extraNRData },
            camelTransformedRespone,
            true,
            apiRetriedCount,
        );
    } else {
        return nrDownstreamInterceptor(
            { startTime, url: logUrl, opts: options, responseSize, extraNRData },
            data,
            false,
            apiRetriedCount,
        );
    }
}

export async function makeDownstreamApiCall<T extends object = object>(
    { onResponseCb, ...params }: ApiCallParams,
    sendRawResponse = false,
    transformToCamelCase = true,
): Promise<Response<T>> {
    const response = await fetchData<T>(params, DOWNSTREAM_TRIGGER_SOURCE.WEB_APP);
    const { response: data, startTime, logUrl, options, apiRetriedCount, responseSize, extraNRData } = response;
    const responseData = transformToCamelCase ? (camelcaseKeys(data, { deep: true }) as T) : (data as T);
    const responseObj = sendRawResponse ? { ...responseData, rawResponse: data } : responseData;
    let mergedNRData = extraNRData;
    try {
        const additionalNRData = onResponseCb?.(responseObj);
        mergedNRData = additionalNRData ? { ...extraNRData, ...additionalNRData } : extraNRData;
    } catch (err) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_ERROR,
            url: logUrl,
            error: err instanceof Error ? err.name : JSON.stringify(err),
            errorMessage: err instanceof Error ? err.message : JSON.stringify(err),
        });
    }
    return nrDownstreamInterceptor(
        { startTime, url: logUrl, opts: options, responseSize, extraNRData: mergedNRData },
        responseObj,
        false,
        apiRetriedCount,
    ) as Promise<Response<T>>;
}
